import React, { useMemo, useState } from "react";
import "./AcademyHome.css";
import LessonViewer from "./LessonViewer.js";
import LearningPath from "./LearningPath.js";
import ProgressTracker from "./ProgressTracker.js";
import { useOptionalUserStorage } from "../storage/UserStorageContext";

const AVAILABLE_ACADEMY_LESSONS = 24;
const ACADEMY_CATEGORIES = [
  {
    id: "foundations",
    icon: "🧭",
    title: "Trading Foundations",
    description:
      "Learn how markets, orders, volume, liquidity, float, and volatility work.",
    lessons: 9,
    level: "Beginner",
  },
  {
    id: "technical-analysis",
    icon: "📊",
    title: "Technical Analysis",
    description:
      "Study candles, support, resistance, breakouts, VWAP, and MACD.",
    lessons: 5,
    level: "Beginner to Intermediate",
  },
  {
    id: "risk-management",
    icon: "🛡️",
    title: "Risk Management",
    description:
      "Protect capital with risk-to-reward, sizing, stops, targets, and trailing stops.",
    lessons: 5,
    level: "Essential",
  },
  {
    id: "trade-execution",
    icon: "⚡",
    title: "Trade Execution",
    description:
      "Plan trades, confirm entries, read Level 2, and understand short selling.",
    lessons: 4,
    level: "Intermediate",
  },
  {
    id: "trader-mindset",
    icon: "🧠",
    title: "Trader Mindset",
    description:
      "Build patience, emotional control, consistency, and rule-based decisions.",
    lessons: 1,
    level: "All Levels",
  },
];

const FEATURED_LESSONS = [
  {
    id: "understanding-market",
    category: "Trading Foundations",
    title: "Understanding the Market",
    description:
      "Learn why stocks move, who participates in the market, and what creates opportunity.",
    duration: "12 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "how-stocks-move",
    category: "Trading Foundations",
    title: "How Stocks Move",
    description:
      "Learn how supply, demand, volume, liquidity, news, and market participation influence price movement.",
    duration: "14 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "market-orders",
    category: "Trading Foundations",
    title: "Market Orders and Limit Orders",
    description:
      "Learn how market orders and limit orders work, when each may be useful, and how execution price can differ from expectation.",
    duration: "15 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "bid-ask-spread",
    category: "Trading Foundations",
    title: "Bid, Ask, and Spread",
    description:
      "Learn how the bid, ask, and spread work, why they change, and how they affect entries, exits, and execution quality.",
    duration: "14 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "volume-liquidity",
    category: "Trading Foundations",
    title: "Volume and Liquidity",
    description:
      "Learn how trading volume and liquidity affect price movement, spreads, execution quality, slippage, and trade risk.",
    duration: "16 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "relative-volume",
    category: "Trading Foundations",
    title: "Relative Volume",
    description:
      "Learn how relative volume compares current trading activity with normal activity and why it can help confirm unusual market participation.",
    duration: "15 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "float-share-supply",
    category: "Trading Foundations",
    title: "Float and Share Supply",
    description:
      "Learn what stock float means, how available share supply can affect volatility, and why low-float stocks can move quickly.",
    duration: "16 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "volatility-price-range",
    category: "Trading Foundations",
    title: "Volatility and Price Range",
    description:
      "Learn how volatility measures price movement, why some stocks move faster than others, and how volatility affects stops, position size, and trade risk.",
    duration: "16 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "average-true-range",
    category: "Trading Foundations",
    title: "Average True Range (ATR)",
    description:
      "Learn how ATR measures typical price movement and how traders use it to plan stops, position size, and realistic targets.",
    duration: "17 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "risk-reward-ratio",
    category: "Risk Management",
    title: "Risk-to-Reward Ratio",
    description:
      "Learn how to compare planned loss with potential profit and use risk-to-reward to evaluate whether a trade setup is worth taking.",
    duration: "17 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "position-sizing",
    category: "Risk Management",
    title: "Position Sizing",
    description:
      "Learn how to calculate the number of shares to trade using maximum dollar risk and the distance between entry and stop.",
    duration: "18 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "trade-planning",
    category: "Trade Preparation",
    title: "Trade Planning",
    description:
      "Learn how to define the setup, entry, stop, target, position size, and invalidation level before entering a trade.",
    duration: "18 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "entry-confirmation",
    category: "Trade Preparation",
    title: "Entry Confirmation",
    description:
      "Learn how to wait for price, volume, momentum, and market conditions to confirm a setup before entering a trade.",
    duration: "18 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "profit-targets",
    category: "Trade Management",
    title: "Profit Targets",
    description:
      "Learn how to set realistic profit targets using support, resistance, volatility, risk-to-reward, and partial profit planning.",
    duration: "18 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "trailing-stops",
    category: "Trade Management",
    title: "Trailing Stops",
    description:
      "Learn how trailing stops can protect profits while allowing a winning trade to continue moving in your favor.",
    duration: "18 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "breakouts-false-breakouts",
    category: "Technical Analysis",
    title: "Breakouts and False Breakouts",
    description:
      "Learn how to identify valid breakouts, recognize warning signs of false breakouts, and wait for stronger confirmation before entering.",
    duration: "20 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "vwap-basics",
    category: "Technical Analysis",
    title: "VWAP Basics",
    description:
      "Learn how volume-weighted average price can help traders evaluate trend, intraday support and resistance, entry quality, and trade location.",
    duration: "20 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "level-2-basics",
    category: "Market Mechanics",
    title: "Level 2 Basics",
    description:
      "Learn how Level 2 data displays bids, asks, market depth, order pressure, liquidity, and possible support or resistance.",
    duration: "20 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "trading-psychology-discipline",
    category: "Trading Mindset",
    title: "Trading Psychology and Discipline",
    description:
      "Learn how emotions, patience, consistency, preparation, and rule-following affect trading decisions and long-term performance.",
    duration: "20 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "candlestick-basics",
    category: "Technical Analysis",
    title: "Candlestick Basics",
    description:
      "Learn how candle bodies, wicks, opening prices, and closing prices reveal buying and selling pressure.",
    duration: "14 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "support-resistance",
    category: "Technical Analysis",
    title: "Support and Resistance Basics",
    description:
      "Learn how price levels form, how traders recognize reactions, and how breakouts and rejections develop.",
    duration: "16 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "macd-basics",
    category: "Technical Analysis",
    title: "MACD: Momentum and Confirmation",
    description:
      "Understand MACD crossovers, momentum shifts, and why price can rise while MACD falls.",
    duration: "15 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "stop-loss",
    category: "Risk Management",
    title: "How to Use a Stop Loss",
    description:
      "Learn where stops belong, how much to risk, and why random stop placement can fail.",
    duration: "10 min",
    level: "Beginner",
    progress: 0,
  },
  {
    id: "short-selling",
    category: "Trade Execution",
    title: "Introduction to Short Selling",
    description:
      "Learn how short selling works, its risks, borrow requirements, and exit planning.",
    duration: "18 min",
    level: "Intermediate",
    progress: 0,
  },
];

const CATEGORY_STAGE_MAP = {
  foundations: "stage-1",
  "technical-analysis": "stage-2",
  "risk-management": "stage-3",
  "trade-execution": "stage-4",
  "trader-mindset": "stage-5",
};

function isLessonComplete(lessonId, storage) {
  return (
    storage.getItem(
      `aninaAcademyLesson:${lessonId}`
    ) === "complete"
  );
}

function AcademyHome() {
  const authenticatedStorage = useOptionalUserStorage();
  const storage = authenticatedStorage || window.localStorage;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showLearningPath, setShowLearningPath] = useState(false);
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [, setProgressRevision] = useState(0);

  const lessonsWithProgress = FEATURED_LESSONS.map((lesson) => {
    const complete = isLessonComplete(lesson.id, storage);

    return {
      ...lesson,
      progress: complete ? 100 : lesson.progress,
      status: complete
        ? "✓ Lesson Complete"
        : lesson.progress > 0
          ? "Continue"
          : "Start Lesson",
      complete,
    };
  });

  const completedLessons = lessonsWithProgress.filter(
    (lesson) => lesson.complete
  ).length;

  const availableLessons = lessonsWithProgress.length;

  const overallProgress =
    availableLessons > 0
      ? Math.round(
          (completedLessons / availableLessons) * 100
        )
      : 0;

  const filteredLessons = useMemo(() => {
    const normalizedSearch = searchTerm
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return lessonsWithProgress;
    }

    return lessonsWithProgress.filter((lesson) => {
      return (
        lesson.title
          .toLowerCase()
          .includes(normalizedSearch) ||
        lesson.category
          .toLowerCase()
          .includes(normalizedSearch) ||
        lesson.description
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });
  }, [searchTerm, lessonsWithProgress]);

  const handleBackToAcademy = () => {
    setSelectedLesson(null);
    setProgressRevision((current) => current + 1);
  };

  const handleContinueLearning = () => {
    const nextLesson =
      lessonsWithProgress.find(
        (lesson) =>
          !lesson.complete && lesson.progress > 0
      ) ||
      lessonsWithProgress.find(
        (lesson) => !lesson.complete
      ) ||
      lessonsWithProgress[0];

    setSelectedLesson(nextLesson);
  };

  const handleSelectLearningPathLesson = (lessonId) => {
    const lesson = lessonsWithProgress.find(
      (item) => item.id === lessonId
    );

    if (!lesson) {
      return;
    }

    setShowLearningPath(false);
    setSelectedStage(null);
    setSelectedLesson(lesson);
  };

  const handleOpenCategory = (categoryId) => {
    setSelectedStage(
      CATEGORY_STAGE_MAP[categoryId] || null
    );

    setShowLearningPath(true);
  };

  const handleOpenFullLearningPath = () => {
    setSelectedStage(null);
    setShowLearningPath(true);
  };

  const handleBackFromLearningPath = () => {
    setShowLearningPath(false);
    setSelectedStage(null);
    setProgressRevision((current) => current + 1);
  };

  const handleOpenProgressTracker = () => {
    setShowProgressTracker(true);
  };

  const handleBackFromProgressTracker = () => {
    setShowProgressTracker(false);
    setProgressRevision((current) => current + 1);
  };

  const handleSelectProgressLesson = (lesson) => {
    setShowProgressTracker(false);
    setSelectedLesson(lesson);
  };

  if (showProgressTracker) {
    return (
      <ProgressTracker
        lessons={lessonsWithProgress}
        onBack={handleBackFromProgressTracker}
        onSelectLesson={handleSelectProgressLesson}
      />
    );
  }

  if (showLearningPath) {
    return (
      <LearningPath
        completedLessons={completedLessons}
        selectedStage={selectedStage}
        onBack={handleBackFromLearningPath}
        onSelectLesson={handleSelectLearningPathLesson}
      />
    );
  }

  if (selectedLesson) {
    return (
      <LessonViewer
        lesson={selectedLesson}
        onBack={handleBackToAcademy}
      />
    );
  }

  return (
    <section className="academyHome">
      <header className="academyHero">
        <div className="academyHeroContent">
          <span className="academyEyebrow">
            ANINATRADE LEARNING CENTER
          </span>

          <h1>🎓 Academy</h1>

          <p>
            Master trading skills with focused, step-by-step lessons.
          </p>

          <div className="academyHeroActions">
            <button
              type="button"
              className="academyPrimaryButton"
              onClick={handleContinueLearning}
            >
              Continue Learning
            </button>

            <button
              type="button"
              className="academySecondaryButton"
              onClick={handleOpenFullLearningPath}
            >
              Learning Path
            </button>

            <button
              type="button"
              className="academySecondaryButton"
              onClick={handleOpenProgressTracker}
            >
              Progress
            </button>
          </div>
        </div>

        <div className="academyProgressCard">
          <span>YOUR PROGRESS</span>

          <div className="academyProgressSummary">
            <strong>{availableLessons} lessons</strong>
            <small>
              {completedLessons} of {AVAILABLE_ACADEMY_LESSONS} completed
            </small>
          </div>

          <div
            className="academyProgressTrack"
            role="progressbar"
            aria-label="Academy completion"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={overallProgress}
          >
            <div
              className="academyProgressFill"
              style={{
                width: `${overallProgress}%`,
              }}
            />
          </div>
        </div>
      </header>

      <section className="academySection">
        <div className="academyDiscoveryRow">
          <div>
            <span className="academySectionEyebrow">
              LEARNING CATEGORIES
            </span>

            <h2>Choose your learning path</h2>
          </div>

          <label className="academySearchBox">
            <span aria-hidden="true">🔍</span>

            <input
              type="search"
              value={searchTerm}
              aria-label="Search Academy lessons"
              placeholder="Search lessons..."
              onChange={(event) =>
                setSearchTerm(event.target.value)
              }
            />
          </label>
        </div>

        <div className="academyCategoryGrid">
          {ACADEMY_CATEGORIES.map((category) => (
            <button
              type="button"
              className="academyCategoryCard"
              key={category.id}
              onClick={() =>
                handleOpenCategory(category.id)
              }
            >
              <div className="academyCategoryIcon">
                {category.icon}
              </div>

              <div>
                <span>{category.level}</span>

                <h3>{category.title}</h3>

                <p>{category.description}</p>
              </div>

              <strong>
                {category.lessons} lessons →
              </strong>
            </button>
          ))}
        </div>
      </section>

      <section className="academySection">
        <div className="academySectionHeader">
          <div>
            <span className="academySectionEyebrow">
              FEATURED LESSONS
            </span>

            <h2>Recommended next steps</h2>
          </div>

          <p>
            Practical lessons selected for your current
            learning stage.
          </p>
        </div>

        <div className="academyLessonGrid">
          {filteredLessons.map((lesson) => (
            <article
              className="academyLessonCard"
              key={lesson.id}
            >
              <div className="academyLessonTopRow">
                <span>{lesson.category}</span>
                <strong>{lesson.duration}</strong>
              </div>

              <h3>{lesson.title}</h3>

              <p>{lesson.description}</p>

              <div className="academyLessonProgress">
                <div className="academyLessonProgressTrack">
                  <div
                    className="academyLessonProgressFill"
                    style={{
                      width: `${lesson.progress}%`,
                    }}
                  />
                </div>

                <span>
                  {lesson.progress}% complete
                </span>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedLesson(lesson)
                }
              >
                {lesson.status}
              </button>
            </article>
          ))}
        </div>

        {filteredLessons.length === 0 && (
          <div className="academyEmptyState">
            <span>🔎</span>

            <h3>No lessons found</h3>

            <p>
              Try searching for another topic such as
              MACD, risk, market, or options.
            </p>
          </div>
        )}
      </section>
    </section>
  );
}

export default AcademyHome;
